import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate } from 'react-router-dom';

const CoWorkingNavBar = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const { t, i18n } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showNavbar, setShowNavbar] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleNavigation = (sectionId: string) => {
        const section = document.getElementById(sectionId);

        if (location.pathname === '/') {
            if (section) {
                section.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            navigate('/', { replace: true });
            setTimeout(() => {
                const targetSection = document.getElementById(sectionId);
                if (targetSection) {
                    targetSection.scrollIntoView({ behavior: 'smooth' });
                }
            }, 300);
        }
    };


    const changeLanguage = (lang: string) => {
        i18n.changeLanguage(lang);
    };

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            if (currentScrollY > lastScrollY && currentScrollY > 100) {
                setShowNavbar(false);
            } else if (currentScrollY < lastScrollY) {
                setShowNavbar(true);
            }

            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll);

        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    useEffect(() => {
        if (isMenuOpen) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }
    }, [isMenuOpen]);

    return (
        <nav className={`fixed top-0 left-0 w-full z-50 transition-transform duration-300 border-b border-[#ccc] ${showNavbar ? 'transform translate-y-0' : 'transform -translate-y-full'} bg-white`}>
            <div className="max-w-screen-xl mx-auto">
                <div className="flex items-center justify-between h-16 md:h-20">
                    {/* Logo */}
                    <div className="flex items-center h-full px-4">
                        <div className="relative h-full flex items-center">
                            <a href="/">
                                <img
                                    src="/assets/images/landing/coWorkingLogo_S.png"
                                    alt="CoWorking Logo Small"
                                    className="block sm:hidden w-[45px]"
                                />
                            </a>
                            <a href="/">
                                <img
                                    src="/assets/images/landing/coWorkingLogo_M.png"
                                    alt="CoWorking Logo Medium"
                                    className="hidden sm:block md:hidden w-[85px]"
                                />
                            </a>
                            <a href="/">
                                <img
                                    src="/assets/images/landing/coWorkingLogo_L.png"
                                    alt="CoWorking Logo Large"
                                    className="hidden md:block pl-4 w-[200px]"
                                />
                            </a>
                        </div>
                    </div>

                    {/* Hamburger menu button */}
                    <div className="md:hidden z-60 h-full flex items-center px-2">
                        <button onClick={toggleMenu} aria-label="Toggle menu">
                            {isMenuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
                        </button>
                    </div>

                    {/* Web menu */}
                    <div className="hidden md:flex items-center">
                        <div className="flex space-x-10 font-bold px-4 items-center">
                            <button onClick={() => handleNavigation('top')} className="text-sm hover:text-gray-400 transition-colors p-2 rounded">{t('cw_lp_nav_bar_1')}</button>
                            <button onClick={() => handleNavigation('concept')} className="text-sm hover:text-gray-400 transition-colors p-2 rounded">{t('cw_lp_nav_bar_2')}</button>
                            <button onClick={() => handleNavigation('news')} className="text-sm hover:text-gray-400 transition-colors p-2 rounded">{t('cw_lp_nav_bar_4')}</button>
                            <button onClick={() => handleNavigation('events')} className="text-sm hover:text-gray-400 transition-colors p-2 rounded">{t('cw_lp_nav_bar_5')}</button>
                            <button onClick={() => handleNavigation('member')} className="text-sm hover:text-gray-400 transition-colors p-2 rounded">{t('cw_lp_nav_bar_10')}</button>
                            <button onClick={() => handleNavigation('access')} className="text-sm hover:text-gray-400 transition-colors p-2 rounded">{t('cw_lp_nav_bar_3')}</button>
                            <button onClick={() => handleNavigation('faq')} className="text-sm hover:text-gray-400 transition-colors p-2 rounded">{t('cw_lp_nav_bar_12')}</button>
                            <button onClick={() => handleNavigation('contact')} className="text-sm hover:text-gray-400 transition-colors p-2 rounded">{t('cw_lp_nav_bar_11')}</button>

                            <div className="border-l border-gray-300 h-6"></div>

                            <div className="flex space-x-4">
                                <button
                                    onClick={() => changeLanguage('ja')}
                                    className={`rounded-full py-1 px-4 ${i18n.language === 'ja' ? 'bg-black text-white' : 'bg-white text-black border border-black hover:bg-blue-600 hover:text-white'}`}
                                >
                                    {t('cw_lp_nav_bar_8')}
                                </button>
                                <button
                                    onClick={() => changeLanguage('en')}
                                    className={`rounded-full py-1 px-4 ${i18n.language === 'en' ? 'bg-black text-white' : 'bg-white text-black border border-black hover:bg-blue-600 hover:text-white'}`}
                                >
                                    {t('cw_lp_nav_bar_9')}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Mobile menu */}
                    <div className={`md:hidden fixed inset-0 flex flex-col transition-opacity duration-500 ${isMenuOpen ? 'opacity-100' : 'opacity-0'} ${isMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
                        <div className="flex flex-col text-white text-center p-2 min-h-screen h-full bg-black bg-opacity-60">
                            <button onClick={toggleMenu} aria-label="Close menu" className="absolute top-3 right-2 z-60">
                                <XMarkIcon className="w-6 h-6 text-white" />
                            </button>
                            <button onClick={() => { handleNavigation('top'); toggleMenu(); }} className="text-lg py-5">{t('cw_lp_nav_bar_1')}</button>
                            <button onClick={() => { handleNavigation('concept'); toggleMenu(); }} className="text-lg py-5">{t('cw_lp_nav_bar_2')}</button>
                            <button onClick={() => { handleNavigation('news'); toggleMenu(); }} className="text-lg py-5">{t('cw_lp_nav_bar_4')}</button>
                            <button onClick={() => { handleNavigation('events'); toggleMenu(); }} className="text-lg py-5">{t('cw_lp_nav_bar_5')}</button>
                            <button onClick={() => { handleNavigation('member'); toggleMenu(); }} className="text-lg py-5">{t('cw_lp_nav_bar_10')}</button>
                            <button onClick={() => { handleNavigation('access'); toggleMenu(); }} className="text-lg py-5">{t('cw_lp_nav_bar_3')}</button>
                            <button onClick={() => { handleNavigation('faq'); toggleMenu(); }} className="text-lg py-5">{t('cw_lp_nav_bar_12')}</button>
                            <button onClick={() => { handleNavigation('contact'); toggleMenu(); }} className="text-lg py-5">{t('cw_lp_nav_bar_11')}</button>

                            <div className="flex justify-center space-x-4 my-4">
                                <button
                                    onClick={() => changeLanguage('ja')}
                                    className={`rounded-full py-1 px-4 ${i18n.language === 'ja' ? 'bg-black text-white' : 'bg-white text-black border border-black hover:bg-blue-600 hover:text-white'}`}
                                >
                                    {t('cw_lp_nav_bar_8')}
                                </button>
                                <button
                                    onClick={() => changeLanguage('en')}
                                    className={`rounded-full py-1 px-4 ${i18n.language === 'en' ? 'bg-black text-white' : 'bg-white text-black border border-black hover:bg-blue-600 hover:text-white'}`}
                                >
                                    {t('cw_lp_nav_bar_9')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default CoWorkingNavBar;
